/**
 * Workouts page (iter69 rewrite) — Daily Adaptive Flow.
 *
 * BIG PIVOT from the old "Week Plan" model: there is no upfront 7-day grid
 * anymore. Each day Nikki proposes ONE session based on yesterday + recovery
 * + check-in. The page has three sections:
 *
 *   1. Today's Session     — fed by /api/workouts/today-suggestion (LLM)
 *   2. This week / month   — completed history dots, modality-colored
 *   3. Library             — saved workouts (filtered by modality)
 *
 * DELETED in this iter (no longer used):
 *   • WeekTab, SwapDayDialog, the 7-dot upfront strip, training_days_per_week setup UI
 *   • TodayHero (replaced by Today's Session card)
 *   • CAT_META category coloring (replaced by modality coloring)
 *
 * Backend pieces kept temporarily (deprecated): /api/week-plan/* endpoints
 * still respond so in-flight production data doesn't 500.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Play, ChevronRight, BookmarkPlus, Dumbbell, Flame, MessageCircle, CircleDot, Timer } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import WorkoutMuscleMap, { musclesFor } from '../components/WorkoutMuscleMap';
import api from '../services/api';

// Modality metadata — colors + labels. Single source for UI.
const MODALITY = {
  strength_upper:     { label: 'Upper Strength', color: '#CCFF00' },
  strength_lower:     { label: 'Lower Strength', color: '#CCFF00' },
  strength_full:      { label: 'Full Body',      color: '#CCFF00' },
  hybrid:             { label: 'Hybrid',         color: '#FBBF24' },
  mobility:           { label: 'Mobility',       color: '#22D3EE' },
  yoga:               { label: 'Yoga',           color: '#A855F7' },
  breathwork:         { label: 'Breathwork',     color: '#60A5FA' },
  injury_management:  { label: 'Injury Care',    color: '#F87171' },
  walk:               { label: 'Walk · Run',     color: '#34D399' },
};

const modalityMeta = (m) => MODALITY[m] || { label: m || 'Session', color: '#888' };

// Which player a modality uses. Mirrors backend ENTRY_MODE.
const entryModeFor = (m) => (m === 'walk' ? 'timer' : m === 'breathwork' ? 'guided' : 'camera');

// ─────────────────────────────────────────────────────────────────────────
// TODAY'S SESSION CARD
// ─────────────────────────────────────────────────────────────────────────
const TodaySessionCard = ({ suggestion, why, onStart, onAskNikki, onSave }) => {
  const meta = modalityMeta(suggestion?.modality);
  if (!suggestion) {
    return (
      <div className="hpme-card p-6" data-testid="today-session-empty">
        <div className="hpme-card-corner" />
        <p className="text-zinc-400">Nikki is putting today's focus together…</p>
      </div>
    );
  }
  const muscles = musclesFor(suggestion, suggestion.modality);
  return (
    <div className="hpme-card p-6" data-testid="today-session-card">
      <div className="hpme-card-corner" />
      <div className="grid md:grid-cols-[1fr_200px] gap-6">
        {/* Left: details */}
        <div className="space-y-4 min-w-0">
          <div>
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.2em] font-secondary font-bold mb-2"
                 style={{ color: meta.color, backgroundColor: `${meta.color}1A`, border: `1px solid ${meta.color}33` }}
                 data-testid="today-modality-badge">
              {meta.label} · {suggestion.duration_min} min
            </div>
            <h2 className="font-secondary font-bold text-2xl text-white leading-tight" data-testid="today-title">{suggestion.title}</h2>
            {(suggestion.why || why) && (
              <p className="text-sm text-zinc-300 leading-relaxed mt-2 border-l-2 pl-3" style={{ borderColor: meta.color }} data-testid="today-why">
                {suggestion.why || why}
              </p>
            )}
          </div>

          {suggestion.exercises?.length > 0 && (
            <div className="space-y-1.5 pt-2 border-t border-zinc-800">
              {suggestion.exercises.map((ex, i) => {
                // Show a clear badge so the user knows BEFORE they start whether
                // this movement will have reps auto-counted or is follow-along.
                const trackable = ex.trackable === true;
                return (
                  <div key={i} className="flex items-baseline gap-2 text-sm" data-testid={`today-exercise-${i}`}>
                    <span className="text-zinc-500 text-[10px] tabular-nums w-5">{String(i+1).padStart(2,'0')}</span>
                    <span className="text-zinc-200 flex-1">{ex.name}</span>
                    {ex.trackable !== undefined && (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wider ${
                          trackable
                            ? 'bg-primary/10 text-primary border border-primary/30'
                            : 'bg-zinc-800 text-zinc-400 border border-zinc-700'
                        }`}
                        title={trackable ? 'Reps auto-counted by camera' : 'Follow-along — no rep counter'}
                        data-testid={`today-exercise-${i}-trackable`}
                      >
                        {trackable ? <CircleDot className="w-2.5 h-2.5" /> : <Timer className="w-2.5 h-2.5" />}
                        {trackable ? 'Rep counted' : 'Follow along'}
                      </span>
                    )}
                    {ex.reps_or_seconds && <span className="text-zinc-500 text-xs">{ex.reps_or_seconds}</span>}
                  </div>
                );
              })}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <button
              type="button"
              onClick={onStart}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary text-black font-secondary font-bold text-xs uppercase tracking-wider rounded-sm hover:bg-primary/90"
              data-testid="today-start-btn"
            >
              <Play className="w-3.5 h-3.5" /> Start session
            </button>
            <button
              type="button"
              onClick={onAskNikki}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs uppercase tracking-wider rounded-sm"
              data-testid="today-swap-btn"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Ask for something else
            </button>
            {onSave && (
              <button
                type="button"
                onClick={onSave}
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-700 hover:border-zinc-500 text-zinc-300 text-xs uppercase tracking-wider rounded-sm"
                data-testid="today-save-btn"
              >
                <BookmarkPlus className="w-3.5 h-3.5" /> Save to library
              </button>
            )}
          </div>
        </div>

        {/* Right: muscle-group body map — which body parts this session works */}
        <div className="hidden md:flex flex-col items-center justify-center" data-testid="today-muscle-map">
          <div className="h-56 w-full">
            <WorkoutMuscleMap primary={muscles.primary} secondary={muscles.secondary} size="sm" showBack />
          </div>
          <div className="flex flex-wrap gap-1 justify-center mt-2 max-w-[200px]">
            {muscles.primary.slice(0, 5).map((mg) => (
              <span key={mg} className="px-1.5 py-0.5 rounded-sm text-[9px] uppercase tracking-wide bg-primary/10 text-primary border border-primary/20">
                {mg.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// COMPLETED HISTORY — compact streak strip + readable clickable list
// ─────────────────────────────────────────────────────────────────────────
const feltLabel = (s) => {
  const sm = s.summary || {};
  const eff = sm.perceived_effort;
  if (eff >= 1 && eff <= 5) return ['very easy', 'easy', 'moderate', 'hard', 'all-out'][eff - 1];
  const mf = sm.muscle_fatigue;
  if (typeof mf === 'number') {
    if (mf < 25) return 'fresh';
    if (mf < 45) return 'light';
    if (mf < 65) return 'moderate';
    if (mf < 80) return 'sore';
    return 'very sore';
  }
  return null;
};

const dateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

const HistorySection = ({ sessions, onOpen }) => {
  // Readable list: most-recent completed sessions first.
  const recent = useMemo(() =>
    [...sessions].sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || '')).slice(0, 8),
    [sessions]);

  return (
    <div className="hpme-card p-5" data-testid="history-section">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-secondary font-bold text-sm uppercase tracking-[0.2em]">Completed</h3>
      </div>

      {/* Readable, clickable list — body image + what + when + how it felt */}
      {recent.length === 0 ? (
        <p className="text-xs text-zinc-500 py-4 text-center">No sessions yet. Start today's and it'll show here.</p>
      ) : (
        <div className="space-y-1.5" data-testid="completed-list">
          {recent.map((s) => {
            const m = modalityMeta(s.modality);
            const muscles = musclesFor(s, s.modality);
            const felt = feltLabel(s);
            return (
              <button
                key={s.id}
                onClick={() => onOpen(s.id)}
                className="w-full flex items-center gap-3 px-2.5 py-2 rounded-sm border border-zinc-800 hover:border-zinc-600 transition-colors text-left group"
                data-testid={`completed-item-${s.id}`}
              >
                <div className="h-11 w-8 shrink-0">
                  <WorkoutMuscleMap primary={muscles.primary} secondary={muscles.secondary} size="sm" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-100 truncate">{s.workout_name || m.label}</p>
                  <p className="text-[10px] flex items-center gap-1.5 flex-wrap">
                    <span style={{ color: m.color }}>{m.label}</span>
                    <span className="text-zinc-600">·</span>
                    <span className="text-zinc-400">{dateLabel(s.completed_at)}</span>
                    {felt && <><span className="text-zinc-600">·</span><span className="text-zinc-400">felt {felt}</span></>}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-primary shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-zinc-500 mt-3">
        {sessions.length} completed
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// LIBRARY — filter by modality
// ─────────────────────────────────────────────────────────────────────────
const LibrarySection = ({ plans, onRun }) => {
  const [filter, setFilter] = useState('all');
  const all = Object.keys(MODALITY);
  const base = filter === 'all' ? plans : plans.filter(p => (p.modality || 'strength_full') === filter);
  // Curated starter sessions (is_seed) first so the Library is never sparse and
  // every modality surfaces good content; then most-recent user/AI plans.
  const filtered = [...base].sort((a, b) => {
    if (!!b.is_seed !== !!a.is_seed) return (b.is_seed ? 1 : 0) - (a.is_seed ? 1 : 0);
    return (b.created_at || '').localeCompare(a.created_at || '');
  });
  return (
    <div className="hpme-card p-5" data-testid="library-section">
      <div className="hpme-card-corner" />
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-secondary font-bold text-sm uppercase tracking-[0.2em]">Library</h3>
      </div>
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {['all', ...all].map(k => {
          const m = k === 'all' ? { label: 'All', color: '#888' } : modalityMeta(k);
          return (
            <button
              key={k} onClick={() => setFilter(k)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm border transition-colors ${filter === k ? 'border-primary text-primary' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}
              data-testid={`library-filter-${k}`}
            >{m.label}</button>
          );
        })}
      </div>
      {filtered.length === 0 ? (
        <p className="text-xs text-zinc-500">Library's empty for that filter — complete a session and tap "Save to library" to add.</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="library-list">
          {filtered.slice(0, 12).map(p => {
            const m = modalityMeta(p.modality);
            const muscles = musclesFor(p, p.modality);
            return (
              <button
                key={p.id} onClick={() => onRun(p)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-sm border border-zinc-800 hover:border-zinc-600 transition-colors text-left group"
                data-testid={`library-item-${p.id}`}
              >
                <div className="h-12 w-9 shrink-0">
                  <WorkoutMuscleMap primary={muscles.primary} secondary={muscles.secondary} size="sm" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-zinc-200 truncate">{p.name}</p>
                  <p className="text-[10px]" style={{ color: m.color }}>{m.label} · {p.duration_minutes || '?'} min</p>
                </div>
                <ChevronRight className="w-3.5 h-3.5 text-zinc-600 group-hover:text-primary shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// PAGE
// ─────────────────────────────────────────────────────────────────────────
const Workouts = () => {
  const navigate = useNavigate();
  const [today, setToday] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [tRes, sRes, pRes, sumRes, metRes] = await Promise.allSettled([
        api.get('/workouts/today-suggestion'),
        api.get('/sessions'),
        api.get('/workouts'),
        api.getPerformanceSummary(),
        api.getPerformanceMetrics(),
      ]);
      let planList = [];
      if (pRes.status === 'fulfilled') planList = pRes.value.data?.workouts || pRes.value.data || [];
      setPlans(planList);
      const planMap = {};
      planList.forEach((p) => { if (p.id) planMap[p.id] = p; });

      // Training-cadence stats (moved here from the dashboard — this is the
      // workout hub, the dashboard is wellness-first now).
      setStats({
        week: sumRes.status === 'fulfilled' ? (sumRes.value.data?.workouts_this_week || 0) : 0,
        total: sumRes.status === 'fulfilled' ? (sumRes.value.data?.total_workouts || 0) : 0,
        streak: metRes.status === 'fulfilled' ? (metRes.value.data?.consistency_breakdown?.current_streak || 0) : 0,
      });

      if (tRes.status === 'fulfilled') setToday(tRes.value.data);
      if (sRes.status === 'fulfilled') {
        const raw = sRes.value.data?.sessions || sRes.value.data || [];
        // Only completed sessions feed the history grid. Attach modality from
        // the session itself or its source plan so the dots are colored right.
        const list = raw.filter((s) => s.completed_at).map((s) => {
          const plan = planMap[s.workout_plan_id] || {};
          const modality = s.modality || plan.modality || 'strength_full';
          return {
            ...s,
            modality,
            primary_muscles: s.primary_muscles || plan.primary_muscles,
            secondary_muscles: s.secondary_muscles || plan.secondary_muscles,
          };
        });
        setSessions(list);
      }
    } catch (e) { /* soft fail */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Route to the right player based on entry mode.
  const routeToSession = (entryMode, sessionId) => {
    if (entryMode === 'timer') navigate(`/timer/${sessionId}`);
    else if (entryMode === 'guided') navigate('/mental');
    else navigate(`/sessions/${sessionId}`);
  };

  const startSession = async () => {
    try {
      const r = await api.post('/workouts/today-suggestion/start');
      routeToSession(r.data.entry_mode, r.data.session_id);
    } catch { toast.error('Could not start the session — try again?'); }
  };

  const saveToLibrary = async () => {
    try {
      const r = await api.post('/workouts/today-suggestion/save');
      toast.success(`Saved "${r.data.name}" to your library.`);
      // Refresh the library list so the new plan shows up.
      const pRes = await api.get('/workouts');
      setPlans(pRes.data?.workouts || pRes.data || []);
    } catch { toast.error('Could not save — try again?'); }
  };

  // Run a saved library plan: spin up a real session, then route by entry mode.
  const runPlan = async (plan) => {
    const entry = plan.entry_mode || entryModeFor(plan.modality);
    if (entry === 'guided') { navigate('/mental'); return; }
    try {
      const r = await api.startSession(plan.id);
      routeToSession(entry, r.data.id);
    } catch { toast.error('Could not start — try again?'); }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6">
          <div className="h-8 w-48 bg-zinc-800 rounded animate-pulse mb-4" />
          <div className="h-64 bg-zinc-800 rounded animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <PageHeader
          tag="Movement"
          tagColor="#CCFF00"
          icon={Activity}
          title="Workouts"
          subtitle="Nikki proposes one session a day based on how you slept, ate, moved and felt yesterday. Tomorrow's session doesn't exist yet — it'll meet you when you arrive."
          testIdPrefix="workouts"
        />

        {/* Training cadence — moved from the dashboard. Counts, not 0-100 scores. */}
        {stats && (
          <div className="grid grid-cols-3 gap-3" data-testid="workout-stats">
            <div className="hpme-card p-4">
              <div className="hpme-card-corner" />
              <div className="flex items-start justify-between mb-2">
                <p className="hpme-label">THIS WEEK</p>
                <Dumbbell className="w-4 h-4 text-primary" />
              </div>
              <span className="hpme-number text-2xl text-primary" data-testid="workouts-week">{stats.week}</span>
            </div>
            <div className="hpme-card p-4">
              <div className="hpme-card-corner" />
              <div className="flex items-start justify-between mb-2">
                <p className="hpme-label">TOTAL</p>
                <Activity className="w-4 h-4 text-green-500" />
              </div>
              <span className="hpme-number text-2xl text-green-500" data-testid="total-workouts">{stats.total}</span>
            </div>
            <div className="hpme-card p-4">
              <div className="hpme-card-corner" />
              <div className="flex items-start justify-between mb-2">
                <p className="hpme-label">STREAK</p>
                <Flame className="w-4 h-4 text-orange-400" />
              </div>
              <span className="hpme-number text-2xl text-orange-400" data-testid="current-streak">{stats.streak}</span>
              <span className="text-zinc-500 text-xs ml-1">day{stats.streak === 1 ? '' : 's'}</span>
            </div>
          </div>
        )}

        <div data-testid="today-focus-section">
          <div className="flex items-center gap-3 mb-1.5">
            <span className="text-[10px] uppercase tracking-[0.2em] text-primary font-secondary font-bold">Today's Focus</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
          <p className="text-xs text-zinc-500 mb-3">Nikki's pick for today — do this to keep your streak.</p>
          <TodaySessionCard
            suggestion={today?.suggestion}
            onStart={startSession}
            onAskNikki={() => navigate('/chat')}
            onSave={saveToLibrary}
          />
        </div>

        <HistorySection sessions={sessions} onOpen={(id) => navigate(`/sessions/${id}`)} />

        <LibrarySection plans={plans} onRun={runPlan} />
      </div>
    </DashboardLayout>
  );
};

export default Workouts;
